import { useRouter } from "next/router";
import { NonSeated } from "~components/reservation";
import {
  Membership,
  MembershipTicketType,
} from "~features/reservation/reservation-types";
import {
  INormalisedData,
  IPromotion,
  IPromotionTicketType,
  IReleaseTicket,
  ITicketTypeKind,
  IReleaseZone,
  TicketTypeType,
} from "../nonseated-reservation.types";
import {
  useCreateGAOrder,
  usePOSIdLocalStorage,
  useQueryFilteredZones,
} from "../nonseated.hooks";
import { MembershipKind, TicketTypeOnSaleStatus } from "~graphql/sdk";
import { useOrganization } from "~hooks";
import { NonSeatedReservationPage } from "~features/reservation/non-seated/NonSeatedReservationPage";
import { getEventVenueLocation } from "~lib/helpers/getEventVenueLocation";

interface INonSeatedMembershipReservation {
  membership: Membership;
  isMembership: boolean;
  isAdmin: boolean;
}

export function NonSeatedMembershipReservation(
  props: INonSeatedMembershipReservation
) {
  const VM = useViewModel(props);
  const { hasFeature } = useOrganization();

  const Component = !hasFeature("LegacyGATicketSelection")
    ? NonSeatedReservationPage
    : NonSeated;

  return (
    <Component
      zones={VM.zones}
      addons={VM.addons}
      data={VM.data}
      createOrder={VM.handleOrderCreation.handleGAOrderCreation}
      isAdmin={props.isAdmin}
      isMembership={true}
      releaseId={undefined}
      membershipId={VM.data.id}
      releaseNotes={undefined}
      allowPublicComp={false}
      isResaleRelease={false}
      releaseType={undefined}
      requiresAuth={false}
    />
  );
}

type Promotion = Membership["multiBuyPromotions"][number];

function useViewModel(props: { membership: Membership }) {
  const query = useRouter()?.query;

  usePOSIdLocalStorage();

  const data = useCreateNormalisedData({
    membership: props.membership,
  });

  const handleOrderCreation = useCreateGAOrder({
    membershipId: props.membership?.id,
  });

  const zones = useQueryFilteredZones(data.zones, query?.zone);

  const addonTypes = data.addons?.filter((addon) => addon.isActive);

  return {
    ...data,
    zones,
    addons: addonTypes,
    handleOrderCreation,
  };
}

function normalisePromotionTicket(
  ticket: Promotion["buyMembershipType"] | Promotion["getMembershipType"]
): IPromotionTicketType {
  return {
    id: ticket.id,
    name: ticket.name,
    description: ticket.description,
    restrictions: ticket.restrictions,
    kind: (ticket.kind as unknown) as ITicketTypeKind,
  };
}

function normalisePromotion(promotion: Promotion): IPromotion {
  return {
    ...promotion,
    buyTicketType: normalisePromotionTicket(promotion.buyMembershipType),
    getTicketType: normalisePromotionTicket(promotion.getMembershipType),
  };
}

function useCreateNormalisedData({ membership }: { membership: Membership }) {
  const normalisedData: INormalisedData = (() => {
    if (membership) {
      return {
        id: membership.id,
        type: "membership",
        name: membership.name,
        gateways: membership.gateways,
        description: membership.description,
        importantNotice: membership.importantNotice,
        multiBuyPromotions: membership.multiBuyPromotions.map(
          normalisePromotion
        ),
        banner: membership.banner,
        backgroundImage: membership.backgroundImage,
        digitalPassCodesStatus: membership.digitalPassCodesStatus,
        isDigital: membership.membershipKind === MembershipKind.Digital,
        ticketResaleWaitlistEnabled: null,
        backgroundImageFullPage: null,
        isR18: null,
        paymentPlanSettings: membership.paymentPlanSettings,
        displayDate: null,
        location: getEventVenueLocation(
          membership.events?.map((event) => event?.venue)
        ),
        fees: membership.fees,
      };
    }
  })();

  function normaliseTicket(
    rzmt: MembershipTicketType,
    zoneName: string,
    zoneId: string
  ): IReleaseTicket {
    const mt = membership.types.find((t) => t.id === rzmt.membershipTypeId);

    if (!mt) return;

    return {
      type: "ticket",
      id: mt.id,
      zoneId: zoneId,
      zoneName: zoneName,
      kind: (mt.kind as unknown) as ITicketTypeKind,
      name: mt.name,
      description: mt.description,
      imageGallery: null,
      restrictions: mt.restrictions,
      image: null,
      price: rzmt.price,
      isSoldOut: false,
      isActive: rzmt.isActive,
      inHighDemand: false,
      purchaseRestrictions: rzmt.purchaseRestrictions,
      purchaseRestrictionsHelpText: rzmt.purchaseRestrictionsHelpText,
      minPurchaseQuantity: mt.minPurchaseQuantity,
      maxPurchaseQuantity: mt.maxPurchaseQuantity,
      fee: mt.ticketFee,
      availabilityStatus: undefined,
      quantity: undefined,
      onSaleStatus: TicketTypeOnSaleStatus.OnSale,
    };
  }

  const zones: IReleaseZone[] = membership?.membershipZones
    .map((rz) => ({
      id: rz.zone.id,
      name: rz.zone.name,
      isActive: rz.isActive,
      pricingLevel: rz.pricingLevel,
      displayOrder: rz.zone.displayOrder,
      description: rz.zone.description?.trim(),
      imageGallery: null,
      ticketTypes: rz.membershipTypes
        .map((tt) => normaliseTicket(tt, rz.zone.name, rz.zone.id))
        .filter((tt) => tt),
    }))
    .sort((a, b) => (a.displayOrder < b.displayOrder ? -1 : 1));

  const addons: IReleaseTicket[] = membership.addons
    .map((ao) => {
      return {
        type: "addon" as TicketTypeType,
        id: ao.id,
        name: ao.name,
        description: ao.description,
        imageGallery: null,
        restrictions: ao.restrictions,
        image: ao.addonImage,
        price: ao.price,
        isActive: ao.isActive,
        isSoldOut: false,
        inHighDemand: false,
        purchaseRestrictions: null,
        purchaseRestrictionsHelpText: null,
        availabilityStatus: null,
        fee: null,
        onSaleStatus: TicketTypeOnSaleStatus.OnSale,
      };
    })
    .filter((ao) => ao);

  return {
    data: normalisedData,
    zones,
    addons,
  };
}
